import path from 'node:path'
import type { ComponentScaffoldOptions } from '@cli-types/next-adapter/init.js'
import { safeWriteFile } from '@cli-utils/files.js'
import { detectProjectName } from '@next-adapter/config/detect/detect-project-name.js'
import { applyAdminNamespaceToContent } from '@next-adapter/config/namespace/apply-admin-namespace-to-content.js'
import { applyAdminNamespaceToPath } from '@next-adapter/config/namespace/apply-admin-namespace-to-path.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { adminDataTemplate } from '@next-adapter/init/templates/app/admin.js'
import { applyNextCacheApiForVersion } from '@next-adapter/template-reader/apply-next-cache-api-for-version.js'
import { readTemplate } from '@next-adapter/template-reader/read-template.js'
import { copyCustomTemplates } from './copy-custom-templates.js'
import { copySchemaMetaschema } from './copy-schema-metaschema.js'
import { copyUiTemplates } from './copy-ui-templates.js'

export function scaffoldComponents({ cwd, config }: ComponentScaffoldOptions): string[] {
  const admin = path.resolve(cwd, config.paths.admin)
  const created: string[] = []
  const namespace = config.frameworkConfig.next.namespace

  function write(relPath: string, content: string): void {
    const namespacedRelPath = applyAdminNamespaceToPath(relPath, namespace)
    const fullPath = path.join(admin, namespacedRelPath)
    const versionedContent = applyNextCacheApiForVersion(content, config.nextMajorVersion)
    if (safeWriteFile(fullPath, applyAdminNamespaceToContent(versionedContent, namespace))) {
      created.push(path.join(config.paths.admin, namespacedRelPath))
    }
  }

  write('admin-globals.css', readTemplate('admin-globals.css'))

  write(
    'components/layouts/admin-providers.tsx',
    readTemplate('components/layouts/admin-providers.tsx')
  )
  write(
    'components/layouts/admin-nav-link.tsx',
    readTemplate('components/layouts/admin-nav-link.tsx')
  )
  write(
    'components/layouts/admin-sidebar.tsx',
    readTemplate('components/layouts/admin-sidebar.tsx')
  )
  write(
    'components/layouts/admin-sidebar-branding.tsx',
    readTemplate('components/layouts/admin-sidebar-branding.tsx')
  )
  write(
    'components/layouts/admin-sidebar-branding-skeleton.tsx',
    readTemplate('components/layouts/admin-sidebar-branding-skeleton.tsx')
  )
  write(
    'components/layouts/admin-sidebar-nav-link.tsx',
    readTemplate('components/layouts/admin-sidebar-nav-link.tsx')
  )
  write(
    'components/layouts/admin-sidebar-nav-link-skeleton.tsx',
    readTemplate('components/layouts/admin-sidebar-nav-link-skeleton.tsx')
  )
  write(
    'components/layouts/admin-settings-sidebar.tsx',
    readTemplate('components/layouts/admin-settings-sidebar.tsx')
  )
  write(
    'components/layouts/admin-sidebar-user-menu.tsx',
    readTemplate('components/layouts/admin-sidebar-user-menu.tsx')
  )
  write(
    'components/layouts/admin-sidebar-user-menu-skeleton.tsx',
    readTemplate('components/layouts/admin-sidebar-user-menu-skeleton.tsx')
  )
  write(
    'components/layouts/sidebar-branding.tsx',
    readTemplate('components/layouts/sidebar-branding.tsx')
  )
  write('components/layouts/admin-header.tsx', readTemplate('components/layouts/admin-header.tsx'))
  write(
    'components/layouts/content-skeleton.tsx',
    readTemplate('components/layouts/content-skeleton.tsx')
  )
  write('components/layouts/role-gate.tsx', readTemplate('components/layouts/role-gate.tsx'))
  write('components/shared/page-header.tsx', readTemplate('components/shared/page-header.tsx'))
  write(
    'components/shared/sort-indicator.tsx',
    readTemplate('components/shared/sort-indicator.tsx')
  )
  write(
    'components/shared/dev-mode-integrate.tsx',
    readTemplate('components/shared/dev-mode-integrate.tsx')
  )
  write(
    'components/shared/dev-mode/copyable-code-block.tsx',
    readTemplate('components/shared/dev-mode/copyable-code-block.tsx')
  )
  write(
    'components/shared/dev-mode/dev-mode-code-mirror.tsx',
    readTemplate('components/shared/dev-mode/dev-mode-code-mirror.tsx')
  )
  write(
    'components/shared/dev-mode/dev-mode-types.ts',
    readTemplate('components/shared/dev-mode/dev-mode-types.ts')
  )
  write(
    'components/shared/dev-mode/plain-code-fallback.tsx',
    readTemplate('components/shared/dev-mode/plain-code-fallback.tsx')
  )
  write(
    'components/shared/dev-mode/lifecycle-hooks-tab.tsx',
    readTemplate('components/shared/dev-mode/lifecycle-hooks-tab.tsx')
  )
  write(
    'components/shared/dev-mode/snippets-tab.tsx',
    readTemplate('components/shared/dev-mode/snippets-tab.tsx')
  )
  write('components/shared/delete-dialog.tsx', readTemplate('components/shared/delete-dialog.tsx'))
  write(
    'components/shared/entity-metadata.tsx',
    readTemplate('components/shared/entity-metadata.tsx')
  )
  write(
    'components/shared/entity-versions/entity-versions-button.tsx',
    readTemplate('components/shared/entity-versions/entity-versions-button.tsx')
  )
  write(
    'components/shared/entity-versions/entity-versions-drawer.tsx',
    readTemplate('components/shared/entity-versions/entity-versions-drawer.tsx')
  )
  write(
    'components/shared/entity-versions/entity-versions-current-row.tsx',
    readTemplate('components/shared/entity-versions/entity-versions-current-row.tsx')
  )
  write(
    'components/shared/entity-versions/entity-version-item.tsx',
    readTemplate('components/shared/entity-versions/entity-version-item.tsx')
  )
  write(
    'components/shared/entity-versions/entity-version-restore-dialog.tsx',
    readTemplate('components/shared/entity-versions/entity-version-restore-dialog.tsx')
  )
  write(
    'components/shared/entity-filters-bar.tsx',
    readTemplate('components/shared/entity-filters-bar.tsx')
  )
  write('components/shared/search-input.tsx', readTemplate('components/shared/search-input.tsx'))
  write(
    'components/shared/sort-order-dialog.tsx',
    readTemplate('components/shared/sort-order-dialog.tsx')
  )
  write(
    'components/shared/sort-order-drag-overlay-item.tsx',
    readTemplate('components/shared/sort-order-drag-overlay-item.tsx')
  )
  write(
    'components/shared/sort-order-item.tsx',
    readTemplate('components/shared/sort-order-item.tsx')
  )
  write(
    'components/shared/sort-order-types.ts',
    readTemplate('components/shared/sort-order-types.ts')
  )
  write(
    'components/shared/media/media-preview.tsx',
    readTemplate('components/shared/media/media-preview.tsx')
  )
  write(
    'components/shared/media/media-grid-item.tsx',
    readTemplate('components/shared/media/media-grid-item.tsx')
  )
  write(
    'components/shared/media/media-grid.tsx',
    readTemplate('components/shared/media/media-grid.tsx')
  )
  write(
    'components/shared/media/media-empty-state.tsx',
    readTemplate('components/shared/media/media-empty-state.tsx')
  )
  write(
    'components/shared/media/edit-media-dialog.tsx',
    readTemplate('components/shared/media/edit-media-dialog.tsx')
  )
  write(
    'components/shared/media/edit-media-dialog-content.tsx',
    readTemplate('components/shared/media/edit-media-dialog-content.tsx')
  )
  write(
    'components/shared/media/media-delete-drawer.tsx',
    readTemplate('components/shared/media/media-delete-drawer.tsx')
  )
  write(
    'components/shared/media/media-delete-dialog.tsx',
    readTemplate('components/shared/media/media-delete-dialog.tsx')
  )
  write(
    'components/shared/media/media-gallery-dialog.tsx',
    readTemplate('components/shared/media/media-gallery-dialog.tsx')
  )
  write(
    'components/shared/media/media-filters-bar.tsx',
    readTemplate('components/shared/media/media-filters-bar.tsx')
  )
  write(
    'components/shared/media/media-url-importer.tsx',
    readTemplate('components/shared/media/media-url-importer.tsx')
  )
  write(
    'components/shared/media/media-grid-pagination.tsx',
    readTemplate('components/shared/media/media-grid-pagination.tsx')
  )
  write(
    'components/shared/data-table/data-table.tsx',
    readTemplate('components/shared/data-table/data-table.tsx')
  )
  write(
    'components/shared/data-table/data-grid.tsx',
    readTemplate('components/shared/data-table/data-grid.tsx')
  )
  write(
    'components/shared/data-table/data-grid-header.tsx',
    readTemplate('components/shared/data-table/data-grid-header.tsx')
  )
  write(
    'components/shared/data-table/data-grid-body.tsx',
    readTemplate('components/shared/data-table/data-grid-body.tsx')
  )
  write(
    'components/shared/data-table/data-grid-row.tsx',
    readTemplate('components/shared/data-table/data-grid-row.tsx')
  )
  write(
    'components/shared/data-table/data-grid-head.tsx',
    readTemplate('components/shared/data-table/data-grid-head.tsx')
  )
  write(
    'components/shared/data-table/data-grid-cell.tsx',
    readTemplate('components/shared/data-table/data-grid-cell.tsx')
  )
  write(
    'components/shared/data-table/data-table-pagination.tsx',
    readTemplate('components/shared/data-table/data-table-pagination.tsx')
  )
  const uiCreated = copyUiTemplates(cwd, config)
  created.push(...uiCreated)

  const customCreated = copyCustomTemplates(cwd, config)
  created.push(...customCreated)

  write('types/index.ts', readTemplate('types/index.ts'))
  write('types/auth.ts', readTemplate('types/auth.ts'))
  write('types/navigation.ts', readTemplate('types/navigation.ts'))
  write('types/table-meta.ts', readTemplate('types/table-meta.ts'))
  write('types/webhooks.ts', readTemplate('types/webhooks.ts'))

  for (const relPath of [
    'utils/storage/decode-storage-key.ts',
    'utils/storage/storage-error.ts',
    'lib/actions/auth/auth.ts',
    'lib/actions/auth/client.ts',
    'lib/actions/auth/middleware.ts',
    'lib/actions/media/clamp-media-page-size.ts',
    'lib/actions/media/clamp-media-page.ts',
    'lib/actions/users/get-cached-users.ts',
    'utils/auth/can-access-admin.ts',
    'utils/auth/get-admin-post-login-path.ts',
    'utils/auth/has-required-role.ts',
    'utils/auth/is-user-role.ts',
    'utils/audit/get-changed-field-names.ts',
    'utils/audit/get-touched-field-names.ts',
    'utils/audit/normalize-comparable-value.ts',
    'utils/audit/should-track-field.ts',
    'utils/date/build-date-range.ts',
    'utils/date/date-range-end-time.ts',
    'utils/date/date-range-input-format.ts',
    'utils/date/date-range-start-time.ts',
    'utils/date/format-date-range-input-date.ts',
    'utils/date/format-date-range-param-date.ts',
    'utils/date/format-date-range-trigger-date.ts',
    'utils/date/get-date-range-trigger-label.ts',
    'utils/date/get-exclusive-date-upper-bound.ts',
    'utils/date/get-ordered-date-range.ts',
    'utils/date/normalize-date-range-time.ts',
    'utils/date/parse-date-input-value.ts',
    'utils/date/parse-editable-date-range-date.ts',
    'utils/dev-mode/code-block-height.ts',
    'utils/error/read-string-property.ts',
    'utils/error/recognize.ts',
    'utils/error/to-action-error.ts',
    'utils/editor/active-table-cell-class.ts',
    'utils/editor/active-table-cell.ts',
    'utils/editor/active-table-wrapper-class.ts',
    'utils/editor/append-column-to-table.ts',
    'utils/editor/append-row-to-table.ts',
    'utils/editor/apply-cursor-change-if-available.ts',
    'utils/editor/apply-cursor-change.ts',
    'utils/editor/can-insert-content-editor-media-placeholder.ts',
    'utils/editor/checkbox-label-factory.ts',
    'utils/editor/clear-table-part-preview.ts',
    'utils/editor/content-editor-highlight-color-name-pattern.ts',
    'utils/editor/content-editor-highlight-color-option.ts',
    'utils/editor/content-editor-highlight-color-options.ts',
    'utils/editor/content-editor-highlight-color.ts',
    'utils/editor/content-editor-highlight-colors.ts',
    'utils/editor/content-editor-highlight.ts',
    'utils/editor/content-editor-inline-math-delimiter.ts',
    'utils/editor/content-editor-inline-math.ts',
    'utils/editor/content-editor-keyboard.ts',
    'utils/editor/content-editor-math-kind.ts',
    'utils/editor/content-editor-math-value.ts',
    'utils/editor/content-editor-media-placeholder-node.ts',
    'utils/editor/content-editor-media-shortcut-key.ts',
    'utils/editor/content-editor-mode.ts',
    'utils/editor/content-editor-raw-content-block.ts',
    'utils/editor/content-editor-selected-math.ts',
    'utils/editor/content-editor-selection-request.ts',
    'utils/editor/content-editor-subscript.ts',
    'utils/editor/content-editor-superscript.ts',
    'utils/editor/content-editor-table-alignment.ts',
    'utils/editor/content-editor-underline.ts',
    'utils/editor/create-content-editor-table-node.ts',
    'utils/editor/create-html-mark.ts',
    'utils/editor/create-markdown-image-text.ts',
    'utils/editor/create-markdown-plain-text-offset-map.ts',
    'utils/editor/create-markdown-table-text.ts',
    'utils/editor/create-markdown-text-from-url.ts',
    'utils/editor/default-content-editor-table-cols.ts',
    'utils/editor/default-content-editor-table-rows.ts',
    'utils/editor/doc-position-from-plain-text-offset.ts',
    'utils/editor/editor-view.ts',
    'utils/editor/escape-html.ts',
    'utils/editor/find-highlight-span-range.ts',
    'utils/editor/find-inline-math-start.ts',
    'utils/editor/find-markdown-link-range.ts',
    'utils/editor/find-node-at-position.ts',
    'utils/editor/find-node-position.ts',
    'utils/editor/find-raw-content-start.ts',
    'utils/editor/find-unescaped-character.ts',
    'utils/editor/find-unescaped-sequence.ts',
    'utils/editor/focus-next-node.ts',
    'utils/editor/format-markdown-blockquote-line.ts',
    'utils/editor/format-markdown-heading-line.ts',
    'utils/editor/format-markdown-list-line.ts',
    'utils/editor/format-markdown.ts',
    'utils/editor/format-shortcut-key.ts',
    'utils/editor/get-active-table-column-alignment.ts',
    'utils/editor/get-block-math-expression.ts',
    'utils/editor/get-closest-table-cell.ts',
    'utils/editor/get-content-editor-highlight-color-name.ts',
    'utils/editor/get-content-editor-highlight-color-value.ts',
    'utils/editor/get-editor-element.ts',
    'utils/editor/get-editor-scroll-element.ts',
    'utils/editor/get-event-table-cell.ts',
    'utils/editor/get-inline-math-delimiter.ts',
    'utils/editor/get-markdown-heading-fallback.ts',
    'utils/editor/get-markdown-list-fallback.ts',
    'utils/editor/get-media-label.ts',
    'utils/editor/get-number-attr.ts',
    'utils/editor/get-plain-text-offset-before-doc-position.ts',
    'utils/editor/get-plain-text-offset-before-markdown-offset.ts',
    'utils/editor/get-raw-content-kind.ts',
    'utils/editor/get-raw-content-token.ts',
    'utils/editor/get-selected-block-nodes.ts',
    'utils/editor/get-selected-content-editor-math.ts',
    'utils/editor/get-selected-nodes-of-type.ts',
    'utils/editor/get-selected-table-column-cell-positions.ts',
    'utils/editor/get-selection-table-cell.ts',
    'utils/editor/get-selection-table-element.ts',
    'utils/editor/get-selection-table.ts',
    'utils/editor/get-slash-menu-state.ts',
    'utils/editor/get-string-attr.ts',
    'utils/editor/get-table-element.ts',
    'utils/editor/get-table-from-event-target.ts',
    'utils/editor/get-table-from-wrapper.ts',
    'utils/editor/get-table-position-for-element.ts',
    'utils/editor/get-table-rect-at-position.ts',
    'utils/editor/get-table-wrapper-element.ts',
    'utils/editor/get-task-item-checkbox-label.ts',
    'utils/editor/has-own-attribute.ts',
    'utils/editor/highlight-span-pattern.ts',
    'utils/editor/inline-math-delimiter.ts',
    'utils/editor/inline-math-node-view.ts',
    'utils/editor/insert-component-at-cursor.ts',
    'utils/editor/insert-content-editor-math.ts',
    'utils/editor/insert-content-editor-media-placeholder.ts',
    'utils/editor/insert-content-editor-table.ts',
    'utils/editor/insert-markdown-text-at-position.ts',
    'utils/editor/insert-result.ts',
    'utils/editor/insert-text-at-cursor.ts',
    'utils/editor/is-allowed-uri.ts',
    'utils/editor/is-content-editor-highlight-color.ts',
    'utils/editor/is-content-editor-media-placeholder-active.ts',
    'utils/editor/is-content-editor-table-alignment.ts',
    'utils/editor/is-element-visible-in-editor.ts',
    'utils/editor/is-escaped.ts',
    'utils/editor/is-extension-available.ts',
    'utils/editor/is-mac.ts',
    'utils/editor/is-mark-in-schema.ts',
    'utils/editor/is-markdown-line-start.ts',
    'utils/editor/is-node-in-schema.ts',
    'utils/editor/is-node-type-selected.ts',
    'utils/editor/is-same-slash-menu-state.ts',
    'utils/editor/is-selected-table-visible.ts',
    'utils/editor/is-task-item-checked.ts',
    'utils/editor/is-valid-dollar-inline-math.ts',
    'utils/editor/is-valid-position.ts',
    'utils/editor/is-word-character.ts',
    'utils/editor/mac-symbols.ts',
    'utils/editor/markdown-heading-level.ts',
    'utils/editor/markdown-heading-option.ts',
    'utils/editor/markdown-heading-options.ts',
    'utils/editor/markdown-image-upload-result.ts',
    'utils/editor/markdown-list-option.ts',
    'utils/editor/markdown-list-options.ts',
    'utils/editor/markdown-list-type.ts',
    'utils/editor/markdown-offset-from-plain-text-offset.ts',
    'utils/editor/match-block-math.ts',
    'utils/editor/match-code-fence-start.ts',
    'utils/editor/match-html-block.ts',
    'utils/editor/match-html-mark-syntax.ts',
    'utils/editor/match-inline-math.ts',
    'utils/editor/match-markdown-block-prefix.ts',
    'utils/editor/match-markdown-mark-syntax.ts',
    'utils/editor/match-raw-content-block.ts',
    'utils/editor/mounted-editor-view.ts',
    'utils/editor/normalize-editor-markdown.ts',
    'utils/editor/parse-content-editor-math-input.ts',
    'utils/editor/parse-shortcut-keys.ts',
    'utils/editor/prepare-markdown-for-rich-mode.ts',
    'utils/editor/protocol-config.ts',
    'utils/editor/raw-content-block-node-view.ts',
    'utils/editor/raw-content-kind.ts',
    'utils/editor/raw-content-match.ts',
    'utils/editor/raw-html-block-tags.ts',
    'utils/editor/remove-highlight-span-at-cursor.ts',
    'utils/editor/remove-markdown-link-at-cursor.ts',
    'utils/editor/remove-selected-content-editor-math.ts',
    'utils/editor/render-details-preview.ts',
    'utils/editor/render-latex.ts',
    'utils/editor/render-raw-content-preview.ts',
    'utils/editor/render-simple-markdown-body.ts',
    'utils/editor/run-slash-command.ts',
    'utils/editor/runnable-slash-command.ts',
    'utils/editor/sanitize-html-preview.ts',
    'utils/editor/sanitize-url.ts',
    'utils/editor/select-current-block-content.ts',
    'utils/editor/selection-within-convertible-types.ts',
    'utils/editor/self-closing-html-block-tags.ts',
    'utils/editor/serialize-raw-content-preview.ts',
    'utils/editor/set-active-table-column-alignment.ts',
    'utils/editor/set-element-attribute.ts',
    'utils/editor/set-markdown-link-at-cursor.ts',
    'utils/editor/show-table-part-preview.ts',
    'utils/editor/slash-commands.ts',
    'utils/editor/slash-menu-state.ts',
    'utils/editor/sr-only.ts',
    'utils/editor/strip-html.ts',
    'utils/editor/strip-markdown-block-prefix.ts',
    'utils/editor/table-add-controls-state.ts',
    'utils/editor/table-input.ts',
    'utils/editor/table-part-preview-class.ts',
    'utils/editor/table-part.ts',
    'utils/editor/table-wrapper-class.ts',
    'utils/editor/transform-selected-markdown-lines.ts',
    'utils/editor/update-nodes-attr.ts',
    'utils/editor/update-selected-content-editor-math.ts',
    'utils/email/escape-html.ts',
    'utils/email/form-email-delivery-input.ts',
    'utils/email/format-email-value.ts',
    'utils/email/is-valid-email.ts',
    'utils/email/normalize-email-list.ts',
    'utils/email/render-form-submission-rows.ts',
    'utils/email/render-notification-email.ts',
    'utils/email/render-thank-you-email.ts',
    'utils/form/clone-default-value.ts',
    'utils/form/create-list-item-key.ts',
    'utils/media/admin-media-type.ts',
    'utils/media/create-admin-media-from-url.ts',
    'utils/media/create-preview-url.ts',
    'utils/media/get-filename-from-url.ts',
    'utils/media/get-media-type.ts',
    'utils/media/get-media-url-help-text.ts',
    'utils/media/get-media-url-placeholder.ts',
    'utils/media/image-extensions.ts',
    'utils/media/image-mime-types.ts',
    'utils/media/infer-media-content-type.ts',
    'utils/media/is-document-only-accept.ts',
    'utils/media/map-media-row.ts',
    'utils/media/media-mime-types.ts',
    'utils/media/revoke-preview-url.ts',
    'utils/media/select-media.ts',
    'utils/media/video-extensions.ts',
    'utils/media/video-mime-types.ts',
    'utils/navigation/order.ts',
    'utils/navigation/sidebar.ts',
    'utils/page/boundary.ts',
    'utils/seo/create-metadata.ts',
    'utils/seo/generate-article-schema.ts',
    'utils/seo/schema-to-json.ts',
    'utils/shared/cn.ts',
    'utils/storage/build-local-storage-key.ts',
    'utils/storage/sanitize-filename.ts',
    'utils/table/default-get-id.ts',
    'utils/table/is-string.ts',
    'utils/text/text.ts',
    'utils/upload/default-remote-accept.ts',
    'utils/upload/dropzone-accept.ts',
    'utils/upload/get-fallback-extension.ts',
    'utils/upload/get-remote-filename.ts',
    'utils/upload/is-accepted-remote-type.ts',
    'utils/upload/is-wildcard-accept.ts',
    'utils/upload/parse-accept-types.ts',
    'utils/upload/parse-dropzone-accept.ts',
    'utils/upload/remote-fetch-timeout-ms.ts',
    'utils/user/compare-user-sort-values.ts',
    'utils/user/get-user-initials.ts',
    'utils/user/get-user-sort-value.ts',
    'utils/user/get-users-page.ts',
    'utils/user/normalize-user-filter-value.ts',
    'utils/validation/file-validation-config.ts',
    'utils/validation/file-validation-error.ts',
    'utils/validation/file-validation-result.ts',
    'utils/validation/format-file-size.ts',
    'utils/validation/is-file-type-allowed.ts',
    'utils/validation/is-valid-url.ts',
    'utils/validation/slugify.ts',
    'utils/validation/validate-files.ts',
    'utils/webhook/create-webhook-event-id.ts',
    'utils/webhook/generate-webhook-secret.ts',
    'utils/webhook/sign-webhook-payload.ts'
  ]) {
    write(relPath, readTemplate(relPath))
  }

  write('hooks/use-upload.ts', readTemplate('hooks/use-upload.ts'))
  write(
    'hooks/content-editor/use-blockquote.ts',
    readTemplate('hooks/content-editor/use-blockquote.ts')
  )
  write(
    'hooks/content-editor/use-code-block.ts',
    readTemplate('hooks/content-editor/use-code-block.ts')
  )
  write(
    'hooks/content-editor/use-color-highlight.ts',
    readTemplate('hooks/content-editor/use-color-highlight.ts')
  )
  write(
    'hooks/content-editor/use-content-editor-source-mode.tsx',
    readTemplate('hooks/content-editor/use-content-editor-source-mode.tsx')
  )
  write(
    'hooks/content-editor/use-content-editor.ts',
    readTemplate('hooks/content-editor/use-content-editor.ts')
  )
  write(
    'hooks/content-editor/use-content-editor-media-insertion.ts',
    readTemplate('hooks/content-editor/use-content-editor-media-insertion.ts')
  )
  write(
    'hooks/content-editor/use-content-editor-table-add-controls.ts',
    readTemplate('hooks/content-editor/use-content-editor-table-add-controls.ts')
  )
  write(
    'hooks/content-editor/use-table-button.ts',
    readTemplate('hooks/content-editor/use-table-button.ts')
  )
  write(
    'hooks/content-editor/use-content-editor-slash-menu.ts',
    readTemplate('hooks/content-editor/use-content-editor-slash-menu.ts')
  )
  write('hooks/content-editor/use-heading.ts', readTemplate('hooks/content-editor/use-heading.ts'))
  write(
    'hooks/content-editor/use-heading-dropdown-menu.ts',
    readTemplate('hooks/content-editor/use-heading-dropdown-menu.ts')
  )
  write(
    'hooks/content-editor/use-link-popover.ts',
    readTemplate('hooks/content-editor/use-link-popover.ts')
  )
  write('hooks/content-editor/use-list.ts', readTemplate('hooks/content-editor/use-list.ts'))
  write(
    'hooks/content-editor/use-list-dropdown-menu.ts',
    readTemplate('hooks/content-editor/use-list-dropdown-menu.ts')
  )
  write('hooks/content-editor/use-mark.ts', readTemplate('hooks/content-editor/use-mark.ts'))
  write(
    'hooks/content-editor/use-menu-navigation.ts',
    readTemplate('hooks/content-editor/use-menu-navigation.ts')
  )
  write(
    'hooks/content-editor/use-undo-redo.ts',
    readTemplate('hooks/content-editor/use-undo-redo.ts')
  )
  write(
    'hooks/content-editor/use-tiptap-editor.ts',
    readTemplate('hooks/content-editor/use-tiptap-editor.ts')
  )
  write('hooks/use-copy-to-clipboard.ts', readTemplate('hooks/use-copy-to-clipboard.ts'))
  write('hooks/use-dev-mode-integration.ts', readTemplate('hooks/use-dev-mode-integration.ts'))
  write('hooks/use-local-storage.ts', readTemplate('hooks/use-local-storage.ts'))
  write('hooks/use-audit-log.ts', readTemplate('hooks/use-audit-log.ts'))
  write('hooks/use-users.ts', readTemplate('hooks/use-users.ts'))
  write('hooks/use-webhooks.ts', readTemplate('hooks/use-webhooks.ts'))
  write('hooks/use-failed-image-url.ts', readTemplate('hooks/use-failed-image-url.ts'))
  write('hooks/use-media.ts', readTemplate('hooks/use-media.ts'))
  write('hooks/use-entity-versions.ts', readTemplate('hooks/use-entity-versions.ts'))
  write('hooks/use-media-filters-bar.ts', readTemplate('hooks/use-media-filters-bar.ts'))
  write('hooks/use-table-utils.ts', readTemplate('hooks/use-table-utils.ts'))
  write(
    'hooks/use-page-boundary-blur-visibility.ts',
    readTemplate('hooks/use-page-boundary-blur-visibility.ts')
  )
  write('hooks/use-page-scroll-threshold.ts', readTemplate('hooks/use-page-scroll-threshold.ts'))

  const adminNamespace = resolveAdminNamespace(namespace)
  const projectName = detectProjectName(cwd)
  write('data/navigation.ts', readTemplate('data/navigation.ts'))
  write('data/webhook-events.ts', readTemplate('data/webhook-events.ts'))
  write(`utils/app/${adminNamespace.camel}.ts`, adminDataTemplate(projectName, namespace))

  for (const relPath of [
    'lib/actions/forms/types.ts',
    'lib/actions/forms/get-form-settings.ts',
    'lib/actions/forms/upsert-form-settings.ts',
    'lib/actions/forms/get-all-form-settings.ts',
    'lib/actions/forms/index.ts',
    'lib/actions/webhooks/types.ts',
    'lib/actions/webhooks/internal-get-active-endpoints.ts',
    'lib/actions/webhooks/create-webhook.ts',
    'lib/actions/webhooks/delete-webhook.ts',
    'lib/actions/webhooks/deliver-to-endpoint.ts',
    'lib/actions/webhooks/deliver-webhook-event.ts',
    'lib/actions/webhooks/emit-webhook-event.ts',
    'lib/actions/webhooks/entity-event-payload.ts',
    'lib/actions/webhooks/get-webhook-deliveries.ts',
    'lib/actions/webhooks/get-webhook-secret.ts',
    'lib/actions/webhooks/get-webhook-subscriptions.ts',
    'lib/actions/webhooks/get-webhooks.ts',
    'lib/actions/webhooks/index.ts',
    'lib/actions/webhooks/prune-webhook-deliveries.ts',
    'lib/actions/webhooks/read-webhook-subscriptions.ts',
    'lib/actions/webhooks/record-webhook-delivery.ts',
    'lib/actions/webhooks/regenerate-webhook-secret.ts',
    'lib/actions/webhooks/register-webhook-hooks.ts',
    'lib/actions/webhooks/send-test-webhook.ts',
    'lib/actions/webhooks/update-webhook.ts',
    'lib/actions/entity-versions/types.ts',
    'lib/actions/entity-versions/get-entity-versions.ts',
    'lib/actions/entity-versions/internal-create-entity-version.ts',
    'lib/actions/entity-versions/internal-delete-entity-versions.ts',
    'lib/actions/entity-versions/index.ts',
    'lib/actions/audit/types.ts',
    'lib/actions/audit/clamp-page.ts',
    'lib/actions/audit/cleanup-audit-logs.ts',
    'lib/actions/audit/first-header-value.ts',
    'lib/actions/audit/get-audit-log-order-by.ts',
    'lib/actions/audit/get-audit-logs.ts',
    'lib/actions/audit/get-audit-request-metadata.ts',
    'lib/actions/audit/get-retention-days.ts',
    'lib/actions/audit/index.ts',
    'lib/actions/audit/max-ip-length.ts',
    'lib/actions/audit/max-user-agent-length.ts',
    'lib/actions/audit/normalize-changed-fields.ts',
    'lib/actions/audit/normalize-page-size.ts',
    'lib/actions/audit/query-audit-logs.ts',
    'lib/actions/audit/record-audit-event.ts',
    'lib/actions/audit/register.ts',
    'lib/lifecycle-hooks/types.ts',
    'lib/lifecycle-hooks/registry.ts',
    'lib/lifecycle-hooks/runner.ts',
    'lib/lifecycle-hooks/register.ts',
    'lib/lifecycle-hooks/register.local.ts',
    'lib/lifecycle-hooks/index.ts',
    'lib/actions/upload/types.ts',
    'lib/actions/upload/upload-files.ts',
    'lib/actions/upload/upload-file.ts',
    'lib/actions/upload/upload-media-from-url.ts',
    'lib/actions/upload/upload-image-from-url.ts',
    'lib/actions/upload/index.ts',
    'lib/actions/users/types.ts',
    'lib/actions/users/change-user-password.ts',
    'lib/actions/users/create-user.ts',
    'lib/actions/users/get-users.ts',
    'lib/actions/users/update-user-role.ts',
    'lib/actions/users/delete-user.ts',
    'lib/actions/users/index.ts',
    'lib/actions/media/types.ts',
    'lib/actions/media/get-media.ts',
    'lib/actions/media/get-media-by-id.ts',
    'lib/actions/media/get-media-by-ids.ts',
    'lib/actions/media/create-media.ts',
    'lib/actions/media/update-media.ts',
    'lib/actions/media/delete-media.ts',
    'lib/actions/media/delete-media-bulk.ts',
    'lib/actions/media/index.ts',
    'lib/actions/profile/types.ts',
    'lib/actions/profile/change-own-password.ts',
    'lib/actions/profile/is-email-configured.ts',
    'lib/actions/profile/invalidate-users-cache.ts',
    'lib/actions/profile/update-email.ts',
    'lib/actions/profile/index.ts'
  ]) {
    write(relPath, readTemplate(relPath))
  }
  for (const relPath of [
    'lib/actions/email/index.ts',
    'lib/actions/email/provider.ts',
    'lib/actions/email/types.ts',
    'lib/actions/email/none.ts',
    'lib/actions/email/is-email-delivery-configured.ts',
    'lib/actions/email/send-email.ts',
    'lib/actions/email/send-password-reset-email.ts',
    'lib/actions/email/form-email-delivery-input.ts',
    'lib/actions/email/queued-form-email.ts',
    'lib/actions/email/send-form-emails.ts',
    'lib/actions/email/send-queued-email.ts',
    'lib/actions/storage/index.ts',
    'lib/actions/storage/provider.ts',
    'lib/actions/storage/types.ts',
    'lib/actions/storage/local.ts',
    'lib/actions/storage/save-upload.ts'
  ]) {
    write(relPath, readTemplate(relPath))
  }

  const schemaCreated = copySchemaMetaschema(cwd, config)
  created.push(...schemaCreated)

  return created
}
